import { ApiError, errorEnvelope } from './http';
import { ApiErrorCodes, type ApiErrorCodeLike } from './errors';
import { MAX_BULK_MESSAGES, type LabelsApi } from './contract';
import {
  LABEL_COLORS,
  type BulkResult,
  type Label,
  type LabelColor,
  type MessageSummary,
  type SieveAction,
} from './types';

/**
 * MOCK: labels (plan/17), for `VITE_USE_MOCKS=true` and tests.
 *
 * It keeps the contract's rules rather than being kinder than it (the HANDOFF
 * lesson): names are normalised and encoded to the same keyword the Go side
 * mints (`labels.Keyword`, §2), keywords compare ASCII case-insensitively,
 * a name whose keyword collides is `409 label_exists`, a PATCH carries its
 * version, a rename/delete leaves the row `renaming`/`deleting` until the
 * client's migrate loop has walked every message, and applying a label that
 * is being deleted is `409 label_busy`.
 *
 * The migrate loop is simulated over steps the way the server runs it: a step
 * rewrites at most `MIGRATE_STEP` messages, and only a step that finds
 * nothing left finishes — so even a label on three messages takes two steps,
 * which is what makes the progress UI reachable in mock mode.
 */

export const MOCK_MAX_LABELS = 200;
export const MOCK_LABEL_KEYWORD_MAX_BYTES = 50;
const MAX_NAME_RUNES = 64;
const MIGRATE_STEP = 25;
const STEP = 65536;

/* ----------------------------------------------------- keyword encoding (§2) */

/**
 * `labels.NormalizeName`: NFC, trimmed, white space runs folded to one space.
 * `undefined` for a name the server refuses (`400 bad_request`).
 */
export function normalizeLabelName(raw: string): string | undefined {
  const name = raw.normalize('NFC').trim().replace(/\s+/gu, ' ');
  if (name === '') return undefined;
  if ([...name].length > MAX_NAME_RUNES) return undefined;
  if (/[\p{Cc}\u2028\u2029"\\]/u.test(name)) return undefined;
  return name;
}

const BASE = 36;
const TMIN = 1;
const TMAX = 26;
const SKEW = 38;
const DAMP = 700;
const INITIAL_BIAS = 72;
const INITIAL_N = 0x20;

function isBasic(code: number): boolean {
  return (
    code === 0x20 ||
    code === 0x2d ||
    code === 0x2e ||
    (code >= 0x30 && code <= 0x39) ||
    (code >= 0x41 && code <= 0x5a) ||
    (code >= 0x61 && code <= 0x7a)
  );
}

function digit(value: number): string {
  return value < 26 ? String.fromCharCode(0x61 + value) : String.fromCharCode(0x30 + value - 26);
}

function adapt(delta: number, points: number, first: boolean): number {
  let d = first ? Math.floor(delta / DAMP) : Math.floor(delta / 2);
  d += Math.floor(d / points);
  let k = 0;
  while (d > ((BASE - TMIN) * TMAX) / 2) {
    d = Math.floor(d / (BASE - TMIN));
    k += BASE;
  }
  return k + Math.floor(((BASE - TMIN + 1) * d) / (d + SKEW));
}

/** RFC 3492's generalised Bootstring with the parameters §2 names. */
function bootstring(name: string): string {
  const input = [...name].map((ch) => ch.codePointAt(0) ?? 0);
  let output = input
    .filter(isBasic)
    .map((code) => (code === 0x20 ? '_' : String.fromCodePoint(code)))
    .join('');
  const basic = output.length;
  let handled = basic;
  if (basic > 0) output += '-';
  let n = INITIAL_N;
  let delta = 0;
  let bias = INITIAL_BIAS;
  while (handled < input.length) {
    const m = Math.min(...input.filter((code) => !isBasic(code) && code >= n));
    delta += (m - n) * (handled + 1);
    n = m;
    for (const code of input) {
      if (code < n || isBasic(code)) delta += 1;
      if (code === n && !isBasic(code)) {
        let q = delta;
        for (let k = BASE; ; k += BASE) {
          const t = k <= bias ? TMIN : k >= bias + TMAX ? TMAX : k - bias;
          if (q < t) break;
          output += digit(t + ((q - t) % (BASE - t)));
          q = Math.floor((q - t) / (BASE - t));
        }
        output += digit(q);
        bias = adapt(delta, handled + 1, handled === basic);
        delta = 0;
        handled += 1;
      }
    }
    delta += 1;
    n += 1;
  }
  return output;
}

/** `labels.Keyword`: the plain form when it can be, else `$label=` + Bootstring. */
export function mockLabelKeyword(name: string): string {
  const plain = [...name].every((ch) => isBasic(ch.codePointAt(0) ?? 0));
  return plain ? `$label_${name.replace(/ /g, '_')}` : `$label=${bootstring(name)}`;
}

/** ASCII case folding, the comparison IMAP keywords get (§2). */
function sameKeyword(a: string, b: string): boolean {
  return a.length === b.length && a.toLowerCase() === b.toLowerCase();
}

/* -------------------------------------------------------------------- store */

interface Row {
  accountId: string;
  label: Label;
  position: number;
}

export interface MockLabelSeed {
  accountId: string;
  name: string;
  color?: LabelColor;
  showInSidebar?: boolean;
}

export interface MockLabelsDeps {
  delay: (signal?: AbortSignal) => Promise<void>;
  newId: (prefix: string) => string;
  accountExists: (accountId: string) => boolean;
  /** The live message store: read AND written (keywords) through this. */
  messages: () => MessageSummary[];
  /**
   * The mailbox's filters, for the first migrate step (§7): the mapper gets
   * each rule's actions and returns the new ones; the answer is how many
   * rules changed, or `undefined` when the mailbox has no Sieve.
   */
  rewriteFilters: (
    accountId: string,
    map: (actions: SieveAction[]) => SieveAction[],
  ) => number | undefined;
  seed?: readonly MockLabelSeed[];
}

export interface MockLabels {
  api: LabelsApi;
  /** Every label of the account, as the server holds them. */
  labelsOf: (accountId: string) => Label[];
  /**
   * `label:` terms pulled out of a search line and resolved (§4): the rest of
   * the line, and a test a message has to pass. Throws `400 unknown_label`.
   */
  labelSearch: (
    accountId: string,
    query: string,
  ) => { text: string; matches: (message: MessageSummary) => boolean };
}

function mockError(
  status: number,
  code: ApiErrorCodeLike,
  message: string,
  details?: Record<string, unknown>,
): ApiError {
  return new ApiError(status, errorEnvelope(code, message, details, 'mock-labels'));
}

function copy(label: Label): Label {
  return { ...label };
}

export function createMockLabels(deps: MockLabelsDeps): MockLabels {
  const rows: Row[] = [];
  /** Per label id: whether this rename/delete has had its filters step yet. */
  const filtersDone = new Set<string>();

  const now = (): string => new Date().toISOString();

  const ofAccount = (accountId: string): Row[] =>
    rows.filter((row) => row.accountId === accountId).sort((a, b) => a.position - b.position);

  const requireAccount = (accountId: string): void => {
    if (!deps.accountExists(accountId)) {
      throw mockError(404, ApiErrorCodes.NotFound, 'Account not found.');
    }
  };

  const requireRow = (accountId: string, labelId: string): Row => {
    const row = rows.find((candidate) => candidate.accountId === accountId && candidate.label.id === labelId);
    if (row === undefined) {
      throw mockError(404, ApiErrorCodes.NotFound, 'Label not found.', { label_id: labelId });
    }
    return row;
  };

  /** The name, normalised and encoded, or the server's refusal. */
  const keywordFor = (raw: unknown): { name: string; keyword: string } => {
    if (typeof raw !== 'string') {
      throw mockError(400, ApiErrorCodes.BadRequest, 'A label needs a name.', { field: 'name' });
    }
    const name = normalizeLabelName(raw);
    if (name === undefined) {
      throw mockError(400, ApiErrorCodes.BadRequest, 'That name cannot be used for a label.', {
        field: 'name',
      });
    }
    const keyword = mockLabelKeyword(name);
    if (new TextEncoder().encode(keyword).length > MOCK_LABEL_KEYWORD_MAX_BYTES) {
      throw mockError(400, 'label_name_too_long', 'That label name is too long.', {
        max_bytes: MOCK_LABEL_KEYWORD_MAX_BYTES,
      });
    }
    return { name, keyword };
  };

  /** A keyword may equal neither another label's keyword nor its previous one. */
  const refuseClash = (accountId: string, keyword: string, except?: string): void => {
    for (const row of ofAccount(accountId)) {
      if (row.label.id === except) continue;
      const clash =
        sameKeyword(row.label.keyword, keyword) ||
        (row.label.previousKeyword !== null && sameKeyword(row.label.previousKeyword, keyword));
      if (clash) {
        throw mockError(409, 'label_exists', 'A label with that name already exists.', {
          label_id: row.label.id,
        });
      }
    }
  };

  const checkColor = (color: unknown): void => {
    if (color !== undefined && !LABEL_COLORS.includes(color as LabelColor)) {
      throw mockError(400, ApiErrorCodes.BadRequest, 'Unknown colour.', { field: 'color' });
    }
  };

  const insert = (accountId: string, input: { name: string; color?: LabelColor; showInSidebar?: boolean }): Label => {
    const { name, keyword } = keywordFor(input.name);
    checkColor(input.color);
    refuseClash(accountId, keyword);
    if (ofAccount(accountId).length >= MOCK_MAX_LABELS) {
      throw mockError(409, 'label_limit', `A mailbox can have at most ${MOCK_MAX_LABELS} labels.`);
    }
    const last = ofAccount(accountId).at(-1);
    const stamp = now();
    const label: Label = {
      id: deps.newId('label'),
      name,
      keyword,
      previousKeyword: null,
      color: input.color ?? 'gray',
      showInSidebar: input.showInSidebar ?? true,
      state: 'active',
      migratedCount: 0,
      version: 1,
      createdAt: stamp,
      updatedAt: stamp,
    };
    rows.push({ accountId, label, position: (last?.position ?? 0) + STEP });
    return label;
  };

  for (const seed of deps.seed ?? []) {
    insert(seed.accountId, {
      name: seed.name,
      ...(seed.color !== undefined ? { color: seed.color } : {}),
      ...(seed.showInSidebar !== undefined ? { showInSidebar: seed.showInSidebar } : {}),
    });
  }

  const touch = (row: Row): void => {
    row.label.version += 1;
    row.label.updatedAt = now();
  };

  const keywordsOf = (message: MessageSummary): string[] => message.labelKeywords ?? [];

  /** One migrate step's message work: at most `MIGRATE_STEP` messages. */
  const rewriteMessages = (row: Row): number => {
    const { label } = row;
    const old = label.state === 'deleting' ? [label.keyword, label.previousKeyword] : [label.previousKeyword];
    const olds = old.filter((keyword): keyword is string => keyword !== null);
    let rewritten = 0;
    for (const message of deps.messages()) {
      if (rewritten >= MIGRATE_STEP) break;
      if (message.accountId !== row.accountId) continue;
      const current = keywordsOf(message);
      if (!current.some((keyword) => olds.some((o) => sameKeyword(o, keyword)))) continue;
      let next = current.filter((keyword) => !olds.some((o) => sameKeyword(o, keyword)));
      if (label.state === 'renaming' && !next.some((keyword) => sameKeyword(keyword, label.keyword))) {
        next = [...next, label.keyword];
      }
      message.labelKeywords = next;
      rewritten += 1;
    }
    return rewritten;
  };

  const api: LabelsApi = {
    async list(accountId, signal) {
      await deps.delay(signal);
      requireAccount(accountId);
      return ofAccount(accountId).map((row) => copy(row.label));
    },

    async create(accountId, input) {
      await deps.delay();
      requireAccount(accountId);
      return copy(insert(accountId, input));
    },

    async update(accountId, labelId, patch) {
      await deps.delay();
      requireAccount(accountId);
      const row = requireRow(accountId, labelId);
      if (patch.version !== row.label.version) {
        throw mockError(409, ApiErrorCodes.Conflict, 'This label was changed elsewhere.', {
          label: copy(row.label),
        });
      }
      if (row.label.state === 'deleting') {
        throw mockError(409, 'label_busy', 'This label is being deleted.');
      }
      checkColor(patch.color);
      if (patch.name !== undefined) {
        const { name, keyword } = keywordFor(patch.name);
        if (!sameKeyword(keyword, row.label.keyword)) {
          if (row.label.state !== 'active') {
            throw mockError(409, 'label_busy', 'This label is still being renamed.');
          }
          refuseClash(accountId, keyword, row.label.id);
          row.label.previousKeyword = row.label.keyword;
          row.label.state = 'renaming';
          row.label.migratedCount = 0;
          filtersDone.delete(row.label.id);
        }
        // A case-only rename changes the spelling and nothing on the server.
        row.label.name = name;
        row.label.keyword = keyword;
      }
      if (patch.color !== undefined) row.label.color = patch.color;
      if (patch.showInSidebar !== undefined) row.label.showInSidebar = patch.showInSidebar;
      touch(row);
      return copy(row.label);
    },

    async reorder(accountId, labelIds) {
      await deps.delay();
      requireAccount(accountId);
      const live = ofAccount(accountId).filter((row) => row.label.state !== 'deleting');
      const named = new Set(labelIds);
      if (
        labelIds.length !== live.length ||
        named.size !== labelIds.length ||
        live.some((row) => !named.has(row.label.id))
      ) {
        throw mockError(400, ApiErrorCodes.BadRequest, 'Name every label exactly once.', {
          field: 'label_ids',
        });
      }
      labelIds.forEach((id, index) => {
        const row = live.find((candidate) => candidate.label.id === id)!;
        row.position = (index + 1) * STEP;
      });
      return ofAccount(accountId).map((row) => copy(row.label));
    },

    async remove(accountId, labelId) {
      await deps.delay();
      requireAccount(accountId);
      const row = requireRow(accountId, labelId);
      if (row.label.state !== 'deleting') {
        row.label.state = 'deleting';
        row.label.migratedCount = 0;
        filtersDone.delete(row.label.id);
        touch(row);
      }
      return copy(row.label);
    },

    async migrate(accountId, labelId) {
      await deps.delay();
      requireAccount(accountId);
      const row = requireRow(accountId, labelId);
      const { label } = row;
      const base = { foldersRefused: [], filtersUpdated: 0, filtersSkipped: false };
      if (label.state === 'active' && label.previousKeyword === null) {
        return { ...base, label: copy(label), done: true, rewritten: 0, remainingInStep: 0 };
      }
      let filtersUpdated = 0;
      let filtersSkipped = false;
      if (!filtersDone.has(label.id)) {
        filtersDone.add(label.id);
        const deleting = label.state === 'deleting';
        const olds = [label.keyword, label.previousKeyword].filter((k): k is string => k !== null);
        const changed = deps.rewriteFilters(accountId, (actions) =>
          actions.flatMap((action) => {
            if (action.type !== 'label' || action.value === undefined) return [action];
            const matches = deleting
              ? olds.some((o) => sameKeyword(o, action.value!))
              : label.previousKeyword !== null && sameKeyword(label.previousKeyword, action.value);
            if (!matches) return [action];
            return deleting ? [] : [{ ...action, value: label.keyword }];
          }),
        );
        if (changed === undefined) filtersSkipped = true;
        else filtersUpdated = changed;
      }
      const rewritten = label.state === 'active' ? 0 : rewriteMessages(row);
      label.migratedCount += rewritten;
      if (rewritten === 0) {
        // A full pass found nothing left: the rewrite is finished.
        filtersDone.delete(label.id);
        if (label.state === 'deleting') {
          rows.splice(rows.indexOf(row), 1);
          return { ...base, filtersUpdated, filtersSkipped, label: null, done: true, rewritten, remainingInStep: 0 };
        }
        label.state = 'active';
        label.previousKeyword = null;
        touch(row);
        return { ...base, filtersUpdated, filtersSkipped, label: copy(label), done: true, rewritten, remainingInStep: 0 };
      }
      return {
        ...base,
        filtersUpdated,
        filtersSkipped,
        label: copy(label),
        done: false,
        rewritten,
        remainingInStep: 0,
      };
    },

    async apply(accountId, input) {
      await deps.delay();
      requireAccount(accountId);
      const add = input.add.map((id) => requireRow(accountId, id).label);
      const remove = input.remove.map((id) => requireRow(accountId, id).label);
      for (const label of add) {
        if (label.state === 'deleting') {
          throw mockError(409, 'label_busy', `“${label.name}” is being deleted.`, { label_id: label.id });
        }
      }
      if (input.messageIds.length === 0) {
        throw mockError(400, ApiErrorCodes.BadRequest, 'At least one message id is required.');
      }
      if (input.messageIds.length > MAX_BULK_MESSAGES) {
        throw mockError(400, ApiErrorCodes.BadRequest, 'too many messages in one request', {
          field: 'message_ids',
          max: MAX_BULK_MESSAGES,
        });
      }
      const result: BulkResult = { applied: [], failed: [] };
      for (const messageId of input.messageIds) {
        const message = deps
          .messages()
          .find((candidate) => candidate.accountId === accountId && candidate.id === messageId);
        if (message === undefined) {
          result.failed.push({ id: messageId, code: ApiErrorCodes.NotFound, message: 'Message not found.' });
          continue;
        }
        let next = keywordsOf(message).filter(
          (keyword) =>
            !remove.some(
              (label) =>
                sameKeyword(label.keyword, keyword) ||
                (label.previousKeyword !== null && sameKeyword(label.previousKeyword, keyword)),
            ),
        );
        for (const label of add) {
          if (!next.some((keyword) => sameKeyword(keyword, label.keyword))) next = [...next, label.keyword];
        }
        message.labelKeywords = next;
        result.applied.push({ id: messageId });
      }
      return result;
    },
  };

  const labelSearch = (accountId: string, query: string) => {
    const terms: string[] = [];
    const text = query
      .replace(/(^|\s)label:(?:"([^"]*)"|(\S+))/gi, (_match, lead: string, quoted?: string, bare?: string) => {
        terms.push(quoted ?? bare ?? '');
        return lead;
      })
      .trim();
    const live = ofAccount(accountId).filter((row) => row.label.state !== 'deleting');
    const wanted = terms.map((term) => {
      const name = normalizeLabelName(term);
      const found = live.find(
        (row) =>
          (name !== undefined && row.label.name.toLowerCase() === name.toLowerCase()) ||
          sameKeyword(row.label.keyword, term),
      );
      if (found === undefined) {
        throw mockError(400, 'unknown_label', `There is no label called “${term}”.`, { name: term });
      }
      return found.label;
    });
    return {
      text,
      matches: (message: MessageSummary) =>
        wanted.every((label) =>
          keywordsOf(message).some(
            (keyword) =>
              sameKeyword(keyword, label.keyword) ||
              (label.previousKeyword !== null && sameKeyword(label.previousKeyword, keyword)),
          ),
        ),
    };
  };

  return {
    api,
    labelsOf: (accountId) => ofAccount(accountId).map((row) => copy(row.label)),
    labelSearch,
  };
}
